import { setSwitchUserTypeToCookie } from '@aurora/shared-apollo/helpers/SwitchUserHelper';
import { LoadingVariant } from '@aurora/shared-client/components/common/Loading/enums';
import Loading from '@aurora/shared-client/components/common/Loading/Loading';
import type { LoadingVariantTypeAndProps } from '@aurora/shared-client/components/common/Loading/types';
import type { SwitchUserTypeContextInterface } from '@aurora/shared-client/components/context/SwitchUserTypeContext/SwitchUserTypeContext';
import SwitchUserTypeContext from '@aurora/shared-client/components/context/SwitchUserTypeContext/SwitchUserTypeContext';
import useCookie from '@aurora/shared-client/components/useCookie';
import {
  SDK_DEFAULT_IMPERSONATED_USER_TYPE,
  SDK_KEY_COOKIE_NAME
} from '@aurora/shared-types/developer/constants';
import usePageLoadingGlobalState from '@aurora/shared-client/helpers/ui/PageLoadingGlobalState';
import { LoadingSize, LoadingSpacing } from '@aurora/shared-client/types/enums';
import { clearBranchCookie } from '@aurora/shared-utils/helpers/developer/SwitchBranchCookieHelper';
import dynamic from 'next/dynamic';
import React, { useEffect, useState } from 'react';

const SdkPreviewBanner = dynamic(() => import('../SdkPreviewBanner/SdkPreviewBanner'), {
  ssr: false
});

const loadingVariant: LoadingVariantTypeAndProps = {
  type: LoadingVariant.DOT,
  props: {
    size: LoadingSize.LG,
    spacing: LoadingSpacing.XL
  }
};

interface Props {
  /**
   * Whether the SDK is enabled.
   */
  isSdkEnabled: boolean;
  /**
   * The type of the switched user.
   */
  switchUserTypeInit: string | null;
}

/**
 * Provides a context for switching user types.
 *
 * @author Lucas Fernandez
 */
const SwitchUserTypeContextProvider: React.FC<React.PropsWithChildren<Props>> = ({
  isSdkEnabled,
  switchUserTypeInit,
  children
}) => {
  const [pageLoading] = usePageLoadingGlobalState('pageLoading');
  const [isEnabled] = useCookie(SDK_KEY_COOKIE_NAME);
  const [switchUserType, setSwitchUserType] = useState<string>(
    switchUserTypeInit ?? SDK_DEFAULT_IMPERSONATED_USER_TYPE
  );

  const context: SwitchUserTypeContextInterface = {
    switchUserType: isEnabled ? switchUserType : null,
    clearSwitchUserType: () => setSwitchUserType(null)
  };

  useEffect(() => {
    if (switchUserTypeInit !== switchUserType) {
      setSwitchUserType(switchUserTypeInit);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [switchUserTypeInit]);

  if (pageLoading) {
    return <Loading variant={loadingVariant} />;
  }

  return (
    <SwitchUserTypeContext.Provider value={context}>
      {isSdkEnabled && isEnabled && (
        <SdkPreviewBanner
          userType={switchUserType}
          onSetSwitchUserType={userType => {
            clearBranchCookie();
            setSwitchUserTypeToCookie(userType);
            window.location.reload();
          }}
        />
      )}
      {children}
    </SwitchUserTypeContext.Provider>
  );
};

export default SwitchUserTypeContextProvider;
